"use client"

import { useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"

import { AUTOMATIC_ONBOARDING_VOICE_ID } from "@/lib/voice/onboarding-voice"
import { useAuth } from "@/hooks/auth/use-auth"

export interface Voice {
  id: string
  name: string
  description?: string
  languages: string[]
  previewUrl?: string
  category?: string
  labels?: Record<string, string>
}

export const voicesQueryKey = (tenantId: string) =>
  ["voice", tenantId, "voices"] as const

export function useVoices(options: { allowAutomaticFallback?: boolean } = {}) {
  const t = useTranslations("Onboarding.VoiceAgentConfigurationForm")
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const tenantId =
    user?.impersonated_tenant_id ?? user?.tenant_id ?? "unauthenticated"
  const query = useQuery({
    queryKey: voicesQueryKey(tenantId),
    enabled: isAuthenticated,
    queryFn: async (): Promise<Voice[]> => {
      const response = await fetchWithAuth("/api/voice/voices")
      const data = (await response.json()) as {
        voices?: Voice[]
        error?: string
      }
      if (!response.ok) {
        throw new Error(data.error ?? "Failed to load voices")
      }
      return data.voices ?? []
    },
  })
  const automaticFallback = Boolean(options.allowAutomaticFallback && query.isError && !query.data?.length)
  const voices = automaticFallback
    ? [{ id: AUTOMATIC_ONBOARDING_VOICE_ID, name: t("automaticVoice"), languages: [], description: t("automaticVoiceDescription") }]
    : query.data ?? []
  const refetchVoices = query.refetch

  const getVoice = (id: string) =>
    voices.find((voice) => voice.id === id)

  const refreshVoices = useCallback(
    async () => {
      await refetchVoices()
    },
    [refetchVoices],
  )

  return {
    voices,
    isLoading: isAuthenticated ? query.isPending : false,
    error: !automaticFallback && query.error instanceof Error ? query.error.message : null,
    automaticFallback,
    getVoice,
    refreshVoices,
  }
}
